import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { QualityAlert } from "../models/quality-alert";
import { GP5 } from "../models/gp5";
import { environment } from "../environments/environment";
import { AnalyticsSearchOptions } from "../pages/Analytics/analytics";
import { TriangleDay } from "../models/triangle-day";
import { calculateDateRange } from "./sessionSlice";
import { RootState } from "../store";

// Add these interfaces at the top of the file


interface QAState {
  models: Record<string, QualityAlert>;
  extendedModels: Record<string, QualityAlert>;
  loading: boolean;
  extendedLoading: boolean;
  error: string | null;
  gp5Data: GP5[];
  isLoading: boolean;
}

interface LoadQAPayload {
  id: number;
  data: QualityAlert;
}

interface FetchQALocationParams {
  locationIds?: number[];
  dateFilter?: string | { before: string; after: string };
  useCache?: boolean;
  supplierId: number;
  excludeClosed?: boolean;
}

interface QualityAlertState {
  gp5Data: GP5[];
  isLoading: boolean;
  error: string | null;
}

// API Call Start
const apiGetGP5Data = async (): Promise<GP5[]> => {
  const baseURL = environment.apiURL;
  const response = await axios.get(`${baseURL}/gp5?fields=all`);
  return response.data;
}

// getting the count of quality alerts by location
const apiGetQualityAlertsByLocations = async (locationIds: number[], supplier: number, excludeClosed: boolean = true): Promise<QualityAlert[]> => {
  const baseURL = environment.apiURL;
  var params = '&fields=all'

  const response = await axios.get(`${baseURL}/customer-data/open-issues-by-locations?locations=[${locationIds.toString()}]&supplier=${supplier}${params}`);
  return response.data;
}

const apiGetQATriangleData = async (date: string, supplierId: string, signal:AbortSignal, locationId?: number): Promise<TriangleDay[] | null[]> => {
  const baseURL = environment.apiURL;
  const localTimezoneOffset = new Date().getTimezoneOffset();
  //based on yyyy-mm date string, get the start date and end date of the month
  const [year, month] = date.split("-");

  const startDate = new Intl.DateTimeFormat('en-CA').format(new Date(+year, +month - 1, 1));
  const endDate = new Intl.DateTimeFormat('en-CA').format(new Date(+year, +month, 0));

  var params = `&fields=all&before=${endDate}&after=${startDate}&timezoneOffset=${localTimezoneOffset}`;
  // params += `&token=${localStorage.getItem('auth_token')}&context=supplier`;
  
  // Add location filter if provided
  if (locationId) {
    params += `&location=${locationId}`;
  }

  const response = await axios.get(`${baseURL}/customer-data/quality-alert/quality-triangle?supplier=${supplierId}${params}`, {signal});
  return response.data;
}

const apiGetWeeklyQaData = async (
    end: string,
    start: string,
   
    supplierId: string,
    signal?:AbortSignal,
    locationId?: number
  ): Promise<any> => {
    const baseURL = environment.apiURL;
    const localTimezoneOffset = new Date().getTimezoneOffset();
    //based on yyyy-mm date string, get the start date and end date of the month
    //const [year, month] = date.split("-");
    const startDate = new Date(start);
    const endDate = new Date(end);
    
    let locationParam = '';
    if (locationId) {
      locationParam = `&location=${locationId}`;
    }
    
    const response = await axios.get(
      `${baseURL}/customer-data/quality-alert/quality-triangle?fields=all&supplier=${supplierId}&before=${
        startDate.toISOString().split("T")[0]
      }&after=${
        endDate.toISOString().split("T")[0]
      }&timezoneOffset=${localTimezoneOffset}${locationParam}`
    ,{signal});
    return response.data;
}

const apiGetProactiveAudits = async (
  start: string,
  end: string,
  supplierId: string,
  locationId?: number
): Promise<any> => {
  const baseURL = environment.apiURL;
  const localTimezoneOffset = new Date().getTimezoneOffset();
  //based on yyyy-mm date string, get the start date and end date of the month
  //const [year, month] = date.split("-");
  const startDate = new Date(start);
  const endDate = new Date(end);

  var params = `?fields=all&supplier=${supplierId}&before=${startDate.toISOString().split("T")[0]}`;
  params += `&after=${endDate.toISOString().split("T")[0]}&timezoneOffset=${localTimezoneOffset}&initialAfter=${endDate.toISOString().split("T")[0]}&part.all`;
  
  // Add location filter if provided
  if (locationId) {
    params += `&location=${locationId}`;
  }

  const response = await axios.get(
    `${baseURL}/customer-data/quality-alert/proactive-audit-count${params}`
  );
  return response.data;
}

const apiGetQualityAlertsByLocationId = async (
    locationIds: number[],
    queryOptions: QueryOptions,
    supplier: number,
): Promise<QualityAlert[]> => {
    const baseURL = environment.apiURL;
    const { before, after, notStatus, ignoreDraft } = queryOptions;

    let params = '&fields=all,parts.all,qualityAlertLocationInfo.all,qualityAlertLocationInfo.qualityAlertDiscrepantMaterial.all,';
    params += 'qualityAlertLocationInfo.qualityAlertDiscrepantMaterial.part';


    params += `&before=${before}&after=${after}`;

    params += `&ignoreDraft=${ignoreDraft}`

    const response = await axios.get(
        `${baseURL}/qualityAlerts?locations=[${locationIds.toString()}]${params}`
    );
    return response.data;
}

const apiGetExtendedQualityAlertsById = async (id: number): Promise<QualityAlert> => {
  const baseURL = environment.apiURL;

  const params = `?fields=all,parts.all,qualityAlertLocationInfo.all,attachments.all,attachments.attachment.all,qualityAlertLocationInfo.qualityAlertDiscrepantMaterial.all,attachments.attachment.all,qualityAlertLocationInfo.qualityAlertDiscrepantMaterial.part,supplierDirectives.all,supplierDirectives.createUser.all,supplier.all,location.all,createUser.all,platform.all,supplierContact.all,qualityAlertLocationInfo.location.oemPicture.all,qualityAlertLocationInfo.location.all, qualityAlertLocationInfo.location.oem.all,qualityAlertLocationInfo.actionsTaken.all, qualityAlertLocationInfo.actionsTaken.createUser.all,qualityAlertLocationInfo.actionsTaken.createUser.photoAttachment.all`;

  const response = await axios.get(`${baseURL}/qualityAlerts/${id}${params}`);
  return response.data;
}

const apiGetQualityAlertsUsingAnalyticsSearchOptions = async (options: AnalyticsSearchOptions, dispatch: any): Promise<QualityAlert[]> => {
  const baseURL = environment.apiURL;
  var params = '?fields=all,parts.all,supplier.all,location.all,location.oem.all,createUser.all,qualityAlertLocationInfo.all,qualityAlertLocationInfo.qualityAlertDiscrepantMaterial.all,qualityAlertLocationInfo.qualityAlertDiscrepantMaterial.part&ignoreDraft=true';

  const rangeResult = await dispatch(calculateDateRange(options.timePeriod));
  if (rangeResult.type.endsWith('/rejected')) {
    throw new Error('Failed to calculate date range');
  }
  const range = rangeResult.payload;

  if (range.after) {
    params += `&afterDateInclusive=${options.customStartDate ?? range.after.toISOString()}`;
  }

  if (range.before) {
    params += `&beforeDateInclusive=${options.customEndDate ?? range.before.toISOString()}`;
  }
  if (options.type === 'Quality Alerts') {
    params += '&level=1';
  }
  else if (options.type === 'Near Misses') {
    params += '&level=0'
  }

  params += `&ignoreDraft=true`
  const response = await axios.get(`${baseURL}/qualityAlerts/search${params}`);
  return response.data;
}

const apiSaveQualityAlert = async (qualityAlert: QualityAlert): Promise<QualityAlert> => {
  const baseURL = environment.apiURL;
  const response = await axios.post(`${baseURL}/qualityAlerts`, qualityAlert);
  return response.data;
}
// API Call End
export const getQualityAlertsByLocations = createAsyncThunk(
  'qa/getQualityAlertsByLocations',
  async (params: FetchQALocationParams, { rejectWithValue }) => {
    const response = await apiGetQualityAlertsByLocations(params.locationIds, params.supplierId, params.excludeClosed);
    return response;
  }
);

export const getQATriangleData = createAsyncThunk(
  'qa/getQATriangleData',
  async (params: {dateFilter: string, supplierId: string, signal: AbortSignal, locationId?: number}, { rejectWithValue }) => {
    const response = await apiGetQATriangleData(params.dateFilter, params.supplierId, params.signal, params.locationId);
    return response;
  }
);

export const getWeeklyQaData = createAsyncThunk(
  'qa/getWeeklyQaData',
  async (params: {end: string, start: string, supplierId: string, signal: AbortSignal, locationId?: number}, { rejectWithValue }) => {
    const response = await apiGetWeeklyQaData(params.end, params.start, params.supplierId, params.signal, params.locationId);
    return response;
  }
);

export const getProactiveAudits = createAsyncThunk(
  'qa/getProactiveAudits',
  async (params: {start: string, end: string, supplierId: string, locationId?: number}, { rejectWithValue }) => {
    const response = await apiGetProactiveAudits(params.start, params.end, params.supplierId, params.locationId);
    return response;
  }
);

export const getQualityAlertsUsingAnalyticsSearchOptions = createAsyncThunk(
  'qa/getQualityAlertsUsingAnalyticsSearchOptions',
  async (params: {options: AnalyticsSearchOptions}, { rejectWithValue, dispatch }) => {
    const response = await apiGetQualityAlertsUsingAnalyticsSearchOptions(params.options, dispatch);
    return response;
  }
);

export const saveQualityAlert = createAsyncThunk(
  'qa/saveQualityAlert',
  async (qualityAlert: QualityAlert, { rejectWithValue }) => {
    try {
      const savedQA = await apiSaveQualityAlert(qualityAlert);
      return savedQA;
    } catch (error) {
      return rejectWithValue('Failed to save quality alert');
    }
  }
);

export const getGP5Data = createAsyncThunk(
  'qa/getGP5Data',
  async () => {
    const gp5Data = await apiGetGP5Data();
    return gp5Data;
  }
);

// Fetch quality alerts by location
export const fetchQualityAlertsByLocation = createAsyncThunk(
  'qa/fetchByLocation',
  async (params: FetchQALocationParams, { rejectWithValue }) => {
    try {
      let filter: QueryOptions;
      
      if (typeof params.dateFilter === 'string') {
        const endDate = new Date();
        const startDate = new Date();
        
        if (params.dateFilter === 'custom') {
          return rejectWithValue('Custom date range not properly formatted');
        } else {
          // Handle different date range options
          switch (params.dateFilter) {
            case '30d':
              startDate.setDate(endDate.getDate() - 30);
              break;
            case '60d':
              startDate.setDate(endDate.getDate() - 60);
              break;
            case '90d':
              startDate.setDate(endDate.getDate() - 90);
              break;
            default:
              const days = parseInt(params.dateFilter);
              if (!isNaN(days)) {
                startDate.setDate(endDate.getDate() - days);
              } else {
                startDate.setDate(endDate.getDate() - 30); // Default to 30 days
              }
          }
        }
        
        filter = {
          before: endDate.toISOString(),
          after: startDate.toISOString(),
          notStatus: 'Open Closed',
          ignoreDraft: true,
        };
      } else {
        filter = { ...params.dateFilter, notStatus: 'Open Closed' };
      }
      
      const response = await apiGetQualityAlertsByLocationId(
        params.locationIds,
        filter,
        params.supplierId
      );

      return response;
    } catch (error) {
      console.error('Fetch error:', error); // Add error logging
      return rejectWithValue('Failed to fetch quality alerts by location');
    }
  }
);

// Add new thunk for fetching extended quality alert
export const fetchExtendedQualityAlert = createAsyncThunk(
  'qa/fetchExtended',
  async (id: number, { rejectWithValue, dispatch }) => {
    try {
      const extendedQA = await apiGetExtendedQualityAlertsById(id);
      dispatch(setExtendedQualityAlert(extendedQA));
      return extendedQA;
    } catch (error) {
      return rejectWithValue('Failed to fetch extended quality alert details');
    }
  }
);
  


// Slice
const initialState: QAState = {
  models: {},
  extendedModels: {},
  loading: false,
  extendedLoading: false,
  error: null,
  gp5Data: [],
  isLoading: false,
};

const qaSlice = createSlice({
  name: "qa",
  initialState,
  reducers: {
    // Function to reset the state (optional utility)
    resetQAs: (state) => {
      state.models = {};
      state.loading = false;
      state.error = null;
    },
    logoutQA: (state) => {
      // Reset everything to initial state
      state.models = {};
      state.extendedModels = {};
      state.loading = false;
      state.extendedLoading = false;
      state.error = null;
    },
      
    updateQualityAlert: (state, action: PayloadAction<QualityAlert>) => {
        const qa = action.payload;
      state.models[qa.id] = qa;
    },

    // Add new reducer for extended data
    setExtendedQualityAlert: (state, action: PayloadAction<QualityAlert>) => {
      const qa = action.payload;
      state.extendedModels[qa.id] = qa;
    },
    clearQualityAlerts: (state) => {
      state.models = {};
      state.extendedModels = {};
      state.loading = false;
      state.extendedLoading = false;
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchQualityAlertsByLocation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQualityAlertsByLocation.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        
        // Convert the array to a record object and directly replace the state
        state.models = action.payload.reduce((acc: Record<string, QualityAlert>, qa: QualityAlert) => {
          acc[Number(qa.id)] = qa;
          return acc;
        }, {});
      })
      .addCase(fetchQualityAlertsByLocation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // New cases for fetchExtendedQualityAlert
      .addCase(fetchExtendedQualityAlert.pending, (state) => {
        state.extendedLoading = true;
        state.error = null;
      })
      .addCase(fetchExtendedQualityAlert.fulfilled, (state, action) => {
        state.extendedLoading = false;
        state.error = null;
        if (action.payload) {
          // Only update extendedModels, leave models unchanged
          state.extendedModels[action.payload.id] = {
            ...action.payload,
            qualityAlertLocationInfo: action.payload.qualityAlertLocationInfo.map(newInfo => ({
              ...newInfo,
              actionsTaken: newInfo.actionsTaken || []
            }))
          };
        }
      })
      .addCase(fetchExtendedQualityAlert.rejected, (state, action) => {
        state.extendedLoading = false;
        state.error = action.payload as string;
      });
  },
});

// Export actions and reducer
export const { logoutQA, resetQAs, updateQualityAlert, setExtendedQualityAlert, clearQualityAlerts } = qaSlice.actions;
export default qaSlice.reducer;

// Selectors
export const selectGP5Data = (state: RootState) => state.qa.gp5Data;
export const selectGP5Loading = (state: RootState) => state.qa.isLoading;
export const selectGP5Error = (state: RootState) => state.qa.error;
